/*
  bvo-fit.js — make every frame occupy the same space in a row of tiles.

  THE PROBLEM
  Luxottica delivered the catalogue in two photographic generations on one canvas.
  Newer EANs (8056262…) are machine-trimmed so the frame fills ~94% of the width; older
  ones (8053672…, 805289…, 713132…, most 8056597…) sit at whatever scale they were shot
  at, down to 60%. Each camera angle is framed differently again — the back view sits
  ~9 points higher in its canvas than the front. Measured across ~50 shots the bounding
  box keeps its aspect ratio (2.06–2.90), so this is uniform scaling and vertical drift
  of the same subject, not a real difference in frame size. Side by side, a 60% shot
  renders the glasses at about half the visual area of its 94% neighbour.

  The image files cannot be re-cut (there is no upload route to Shopify from the
  tooling), so the correction happens at render.

  WHY THIS DOES NOT REUSE BVO.optical
  bvo.js exposes BVO.optical.measure — a cached, promise-based bounding box that
  bvo-zoom.js uses for cropping. Reusing it was tried on 2 Sept and reverted, measured:
  it scans at a threshold that includes the soft drop shadow, and going async let
  repeated runs race each other over the shared median. The card grid's spread went from
  2% back to 11%. This file therefore keeps its own synchronous scan. The duplication is
  deliberate and costs one downscaled read of an image the browser already holds.

  DESIGN NOTES
  - The target is the GROUP MEDIAN, not a constant: a card grid converges on its own
    middle, so no magic number needs maintaining and a layout change cannot break it.
  - Threshold 120 excludes the drop shadow. It only adds ~1% of height, but the centre
    should sit on the glasses, not on the glasses plus their shadow.
  - Product-page thumbs set the target and the main image joins it, so switching angle
    or colourway no longer makes the frame hop.
  - Scale and shift are capped, and capped again so the frame can never touch the edge
    of its box.
  - Images are held invisible by CSS until they have been through here, so the
    correction is never seen as a jump. EVERY image must therefore end up marked,
    including ones that cannot be measured — see mark(). layout/theme.liquid also lifts
    the gate after 2.5s, so a failure to load this file cannot leave a page blank.
*/
(function () {
  'use strict';

  var SAMPLE_W  = 160;   // scan width; a bounding box needs no more
  var THRESH    = 120;   // colour distance from the corner pixel; excludes the shadow
  var MIN_S     = 0.75;  // never shrink past this
  var MAX_S     = 1.70;  // never enlarge past this
  var SAFE      = 0.98;  // keep the frame inside this share of its box
  var MAX_SHIFT = 0.20;  // never slide a photo more than this share of its box

  var cache = Object.create(null);

  /* Marking is what makes an image visible again. Anything this file touches gets
     marked, whether or not it could be corrected. */
  function mark(img) {
    if (img && !img.hasAttribute('data-bvo-fitted')) img.setAttribute('data-bvo-fitted', '1');
  }

  function measure(img) {
    var src = img.currentSrc || img.src;
    if (!src) return null;
    if (src in cache) return cache[src];
    if (!img.naturalWidth || !img.naturalHeight) return null;

    var w = SAMPLE_W;
    var h = Math.max(1, Math.round(SAMPLE_W * img.naturalHeight / img.naturalWidth));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    var d;
    try {
      ctx.drawImage(img, 0, 0, w, h);
      d = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      cache[src] = null;   // unreadable: leave this one alone
      return null;
    }

    var br = d[0], bg = d[1], bb = d[2];
    var minX = w, maxX = -1, minY = h, maxY = -1;
    for (var y = 0; y < h; y++) {
      for (var p = 0; p < w; p++) {
        var o = (y * w + p) * 4;
        if (Math.abs(d[o] - br) + Math.abs(d[o + 1] - bg) + Math.abs(d[o + 2] - bb) > THRESH) {
          if (p < minX) minX = p;
          if (p > maxX) maxX = p;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) { cache[src] = null; return null; }   // blank canvas, nothing to centre

    var m = {
      fw: (maxX - minX + 1) / w, fh: (maxY - minY + 1) / h,
      fx: ((minX + maxX) / 2) / w, fy: ((minY + maxY) / 2) / h
    };
    cache[src] = m;
    return m;
  }

  /* Where the photo actually sits inside its element, honouring object-fit. */
  function geom(img) {
    var W = img.offsetWidth, H = img.offsetHeight;
    if (!W || !H || !img.naturalWidth) return null;
    var ar = img.naturalWidth / img.naturalHeight;
    var fit = getComputedStyle(img).objectFit || 'fill';
    var cw, ch;
    if (fit === 'cover') {
      cw = Math.max(W, H * ar); ch = cw / ar;
    } else if (fit === 'contain' || fit === 'scale-down') {
      if (W / H > ar) { ch = H; cw = H * ar; } else { cw = W; ch = W / ar; }
    } else {
      cw = W; ch = H;
    }
    return { W: W, H: H, cw: cw, ch: ch, ox: (W - cw) / 2, oy: (H - ch) / 2 };
  }

  /* Bounding-box area as a share of the element box — the quantity being equalised. */
  function share(img, m) {
    var g = geom(img);
    if (!g) return null;
    return (m.fw * g.cw) * (m.fh * g.ch) / (g.W * g.H);
  }

  function apply(img, m, target) {
    var g = geom(img);
    if (!g) { mark(img); return; }
    var now = (m.fw * g.cw) * (m.fh * g.ch) / (g.W * g.H);
    if (!now) { mark(img); return; }

    var s = Math.sqrt(target / now);
    s = Math.max(MIN_S, Math.min(MAX_S, s));
    var cap = Math.min(SAFE * g.W / (m.fw * g.cw), SAFE * g.H / (m.fh * g.ch));
    if (s > cap) s = cap;

    // scale about the element centre, then pull the frame's own centre onto it
    var cx = g.ox + m.fx * g.cw;
    var cy = g.oy + m.fy * g.ch;
    var tx = -s * (cx - g.W / 2);
    var ty = -s * (cy - g.H / 2);
    var capX = g.W * MAX_SHIFT, capY = g.H * MAX_SHIFT;
    if (tx > capX) tx = capX; else if (tx < -capX) tx = -capX;
    if (ty > capY) ty = capY; else if (ty < -capY) ty = -capY;

    var parent = img.parentElement;
    if (parent && getComputedStyle(parent).overflow === 'visible') parent.style.overflow = 'hidden';

    img.style.transformOrigin = '50% 50%';
    img.style.transform = 'translate(' + tx.toFixed(1) + 'px,' + ty.toFixed(1) + 'px) scale(' + s.toFixed(4) + ')';
    mark(img);
  }

  function median(list) {
    var a = list.slice().sort(function (x, y) { return x - y; });
    var n = a.length;
    if (!n) return null;
    return n % 2 ? a[(n - 1) / 2] : (a[n / 2 - 1] + a[n / 2]) / 2;
  }

  /* Measure a set together, then pull every member onto the set's median.
     Synchronous on purpose: the first full row decides the target and everything that
     arrives later joins it, so nothing already on screen ever shifts twice. */
  function balance(imgs, forcedTarget) {
    var rows = [];
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img) continue;
      if (!img.complete || !img.naturalWidth) continue;   // still loading; the load handler returns
      var m = measure(img);
      if (!m) { mark(img); continue; }                    // unmeasurable: show it untouched
      var sh = share(img, m);
      if (!sh) { mark(img); continue; }
      rows.push({ img: img, m: m, share: sh });
    }
    if (!rows.length) return forcedTarget || null;
    var target = forcedTarget || median(rows.map(function (r) { return r.share; }));
    for (var j = 0; j < rows.length; j++) apply(rows[j].img, rows[j].m, target);
    return target;
  }

  var cardTarget = null;
  var galleryTarget = null;

  function run() {
    // product cards wherever they appear: homepage row, collection grids, search
    cardTarget = balance(
      Array.prototype.slice.call(document.querySelectorAll('.pcard__media img')),
      cardTarget
    );

    // the product gallery: thumbs set the target, the main image joins it, so switching
    // angle or colourway no longer makes the frame hop
    var t = balance(Array.prototype.slice.call(document.querySelectorAll('[data-thumb] img')), galleryTarget);
    if (t) galleryTarget = t;

    var main = document.querySelector('[data-main-image]');
    if (main && main.complete && main.naturalWidth) {
      var mm = measure(main);
      if (mm) apply(main, mm, galleryTarget || share(main, mm));
      else mark(main);
    }
  }

  var pending = null;
  function schedule() {
    if (pending) cancelAnimationFrame(pending);
    pending = requestAnimationFrame(function () { pending = null; try { run(); } catch (e) {} });
  }

  function boot() {
    try { run(); } catch (e) { /* never let this break a page */ }

    // late arrivals: lazy loading, srcset swaps, colourway changes
    document.addEventListener('load', function (e) {
      var t = e.target;
      if (t && t.tagName === 'IMG' && t.closest &&
          t.closest('.pcard__media, [data-thumb], .product__main-media')) schedule();
    }, true);

    // an image that errors must still be revealed
    document.addEventListener('error', function (e) {
      var t = e.target;
      if (t && t.tagName === 'IMG') mark(t);
    }, true);

    // bvo.js rewrites the gallery when a colourway or angle changes
    var main = document.querySelector('[data-main-image]');
    var thumbRow = document.querySelector('[data-thumbs]');
    if (main) new MutationObserver(schedule).observe(main, { attributes: true, attributeFilter: ['src', 'srcset'] });
    if (thumbRow) new MutationObserver(schedule).observe(thumbRow, { childList: true, subtree: true });

    window.addEventListener('resize', schedule);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
